import { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/feature/SeoHead';
import { featuresData, statsData, testimonialsData, faqData, logosData } from '@/mocks/data';

function Hero() {
  return (
    <section className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20composition%20with%20flowing%20organic%20shapes%20in%20coral%20and%20cream%20tones%2C%20soft%20geometric%20overlays%2C%20minimalist%20modern%20aesthetic%2C%20smooth%20transitioning%20color%20fields%2C%20warm%20atmospheric%20glow%2C%20professional%20tech%20vibe%2C%20ethereal%20light%20rays%2C%20clean%20elegant%20design%2C%20no%20text%20or%20typography&width=1800&height=900&seq=jiuyou-hero-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35" />
      </div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-6">
            探索 jiuyou 的
            <br />
            <span className="text-primary-400">全方位体验</span>
          </h1>
          <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            jiuyou是您值得信赖的首选平台。无论您是初次了解还是深度用户，
            我们都为您提供智能、高效、安全的全方位服务解决方案。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/categories"
              className="w-full sm:w-auto whitespace-nowrap px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-medium hover:bg-primary-600 transition-all duration-200"
            >
              立即体验
            </Link>
            <Link
              to="/about"
              className="w-full sm:w-auto whitespace-nowrap px-8 py-3.5 rounded-full border border-background-300 text-background-50 text-base font-medium hover:border-background-50 hover:bg-background-50/10 transition-all duration-200"
            >
              了解更多
            </Link>
          </div>
          <div className="mt-14 flex items-center justify-center gap-8 md:gap-12 flex-wrap">
            {statsData.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">{stat.value}</div>
                <div className="text-xs md:text-sm text-background-300 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-background-300 text-2xl" />
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100/80 text-primary-700 text-xs font-medium mb-4">
            核心功能
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            为什么选择 jiuyou
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            六大核心优势，为您打造极致体验
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featuresData.map((feature) => (
            <div
              key={feature.id}
              className="group p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                <i className={`${feature.icon} text-2xl`} />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">{feature.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustedBy() {
  return (
    <section className="w-full px-4 md:px-6 py-12 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <p className="text-center text-sm text-foreground-500 mb-8">深受以下合作伙伴信赖</p>
        <div className="flex items-center justify-center gap-12 md:gap-20 flex-wrap">
          {logosData.map((logo) => (
            <div
              key={logo.name}
              className="px-6 py-3 rounded-lg bg-background-50 border border-background-200/60"
            >
              <span className="text-sm font-medium text-foreground-500">{logo.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100/80 text-accent-700 text-xs font-medium mb-4">
            用户口碑
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            听听用户怎么说
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            超过500万用户的真实反馈，是我们前进的最大动力
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonialsData.map((testimonial) => (
            <div
              key={testimonial.id}
              className="p-6 rounded-2xl bg-background-50 border border-background-200/70"
            >
              <div className="flex items-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <i
                    key={i}
                    className={`text-sm ${i < testimonial.rating ? 'ri-star-fill text-primary-500' : 'ri-star-fill text-foreground-200'}`}
                  />
                ))}
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-5">{testimonial.content}</p>
              <div className="flex items-center gap-3">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="text-sm font-semibold text-foreground-900">{testimonial.name}</div>
                  <div className="text-xs text-foreground-500">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
          准备好开始您的 jiuyou 之旅了吗？
        </h2>
        <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-xl mx-auto">
          立即注册jiuyou，体验全新一代智能服务平台带来的高效与便捷
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/categories"
            className="w-full sm:w-auto whitespace-nowrap px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-medium hover:bg-primary-600 transition-all duration-200"
          >
            免费注册
          </Link>
          <Link
            to="/articles"
            className="w-full sm:w-auto whitespace-nowrap px-8 py-3.5 rounded-full border border-foreground-300 text-foreground-700 text-base font-medium hover:border-primary-400 hover:text-primary-500 transition-all duration-200"
          >
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100/80 text-secondary-700 text-xs font-medium mb-4">
            常见问题
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            快速解答您的疑惑
          </h2>
        </div>
        <div className="space-y-3">
          {faqData.map((faq, index) => (
            <div
              key={index}
              className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                <i
                  className={`text-lg text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                    openIndex === index ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    const existingScript = document.getElementById('schema-org-website');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'schema-org-website';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'jiuyou',
        url: 'https://jiuyou.com',
        description: 'jiuyou是您值得信赖的首选平台，提供智能、高效、安全的综合服务解决方案。',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-549868',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      });
      document.head.appendChild(script);
    }

    const existingFaqScript = document.getElementById('schema-faq');
    if (!existingFaqScript) {
      const faqScript = document.createElement('script');
      faqScript.id = 'schema-faq';
      faqScript.type = 'application/ld+json';
      faqScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer,
          },
        })),
      });
      document.head.appendChild(faqScript);
    }

    return () => {
      const ws = document.getElementById('schema-org-website');
      const fs = document.getElementById('schema-faq');
      if (ws) ws.remove();
      if (fs) fs.remove();
    };
  }, []);

  return (
    <>
      <SeoHead
        data={{
          title: 'jiuyou官方落地页——探索jiuyou的全方位体验 | jiuyou官网',
          description: 'jiuyou是您值得信赖的首选平台，全面介绍jiuyou的核心功能、服务优势与用户口碑。无论您是初次了解jiuyou还是深度用户，这里都能找到您需要的一切信息，立即体验jiuyou带来的全新感受。',
          canonicalUrl: 'https://jiuyou.com',
          ogType: 'website',
        }}
      />
      <Hero />
      <Features />
      <TrustedBy />
      <Testimonials />
      <CTA />
      <FAQ />
    </>
  );
}